import { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { VALIDATION_ERRORS } from '../const';

/**
 * Custom hook to handle tournament and registration date and time validation
 * @param {object} params - Object containing all required parameters
 * @param {Date} params.calendarDateStart - Tournament start date
 * @param {Date} params.calendarDateEnd - Tournament end date
 * @param {string} params.dateStartTime - Tournament start time
 * @param {string} params.dateFinishTime - Tournament end time
 * @param {Date} params.calendarDateStartReg - Registration start date
 * @param {Date} params.calendarDateEndReg - Registration end date
 * @param {string} params.registrationStartTime - Registration start time
 * @param {string} params.registrationFinishTime - Registration finish time
 * @param {function} params.setDateFinishTime - Setter for tournament finish time
 * @param {function} params.setDateStartTime - Setter for tournament start time
 * @param {function} params.setCalendarDateStartReg - Setter for registration start date
 * @param {function} params.setCalendarDateEndReg - Setter for registration end date
 * @param {function} params.setCalendarDateEnd - Setter for tournament end date
 * @param {function} params.setMatchStartDate - Setter for match start date
 * @param {function} params.setMatchStart - Setter for match start time
 * @param {function} params.setValue - React Hook Form setValue function
 * @returns {object} - Tournament and registration date validation handlers and error states
 */
export const useTournamentDatesValidation = ({
  calendarDateStart,
  calendarDateEnd,
  dateStartTime,
  dateFinishTime,
  calendarDateStartReg,
  calendarDateEndReg,
  registrationStartTime,
  registrationFinishTime,
  setDateFinishTime,
  setDateStartTime,
  setCalendarDateStartReg,
  setCalendarDateEndReg,
  setCalendarDateEnd,
  setMatchStartDate,
  setMatchStart,
  setValue
}) => {
  // Validation error states
  const [registrationStartDateError, setRegistrationStartDateError] = useState("");
  const [registrationEndDateError, setRegistrationEndDateError] = useState("");
  const [tournamentStartTimeError, setTournamentStartTimeError] = useState("");
  const [tournamentFinishTimeError, setTournamentFinishTimeError] = useState("");
  const [registrationTimeError, setRegistrationTimeError] = useState("");
  const [tournamentStartDateError, setTournamentStartDateError] = useState("");
  const [tournamentFinishDateError, setTournamentFinishDateError] = useState("");
  const [registrationDateError, setRegistrationDateError] = useState("");
  const [registrationStartTimeError, setRegistrationStartTimeError] = useState("");
  const [registrationFinishTimeError, setRegistrationFinishTimeError] = useState("");

  // Helper function to compare dates (ignoring time)
  const isSameDate = useCallback((date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }, []);

  // Helper function to compare dates using moment (ignoring time)
  const isDateBefore = useCallback((date1, date2) => {
    return moment(date1).startOf('day').isBefore(moment(date2).startOf('day'));
  }, []);

  // Run validation when tournament dates/times change
  useEffect(() => {
    // Validate tournament dates
    if (isDateBefore(calendarDateEnd, calendarDateStart)) {
      setTournamentStartDateError(VALIDATION_ERRORS.TOURNAMENT_START_DATE_ERROR);
      setTournamentFinishDateError(VALIDATION_ERRORS.TOURNAMENT_FINISH_DATE_ERROR);
    } else {
      setTournamentStartDateError("");
      setTournamentFinishDateError("");
    }

    // Validate registration dates
    if (isDateBefore(calendarDateEndReg, calendarDateStartReg)) {
      setRegistrationDateError(VALIDATION_ERRORS.REGISTRATION_FINISH_BEFORE_START);
    } else {
      setRegistrationDateError("");
    }

    // Validate tournament times
    if (isSameDate(calendarDateStart, calendarDateEnd) && dateFinishTime < dateStartTime) {
      setTournamentFinishTimeError(VALIDATION_ERRORS.TOURNAMENT_FINISH_TIME_BEFORE_START);
    } else {
      setTournamentFinishTimeError("");
    }

    // Validate registration times
    if (isSameDate(calendarDateStartReg, calendarDateEndReg) && registrationFinishTime < registrationStartTime) {
      setRegistrationTimeError(VALIDATION_ERRORS.REGISTRATION_FINISH_TIME_BEFORE_START);
    } else {
      setRegistrationTimeError("");
    }

    // Validate registration start time
    if (isSameDate(calendarDateStartReg, calendarDateStart) && registrationStartTime < dateStartTime) {
      setRegistrationStartTimeError(VALIDATION_ERRORS.REGISTRATION_START_TIME_BEFORE_TOURNAMENT_START);
    } else if (isSameDate(calendarDateStartReg, calendarDateEnd) && registrationStartTime > dateFinishTime) {
      setRegistrationStartTimeError(VALIDATION_ERRORS.REGISTRATION_START_TIME_AFTER_TOURNAMENT_END);
    } else {
      setRegistrationStartTimeError("");
    }

    // Validate registration finish time
    if (isSameDate(calendarDateEndReg, calendarDateStart) && registrationFinishTime < dateStartTime) {
      setRegistrationFinishTimeError(VALIDATION_ERRORS.REGISTRATION_FINISH_TIME_BEFORE_TOURNAMENT_START);
    } else if (isSameDate(calendarDateEndReg, calendarDateEnd) && registrationFinishTime > dateFinishTime) {
      setRegistrationFinishTimeError(VALIDATION_ERRORS.REGISTRATION_FINISH_TIME_AFTER_TOURNAMENT_END);
    } else {
      setRegistrationFinishTimeError("");
    }
  }, [calendarDateStart, calendarDateEnd, dateStartTime, dateFinishTime, calendarDateStartReg, calendarDateEndReg, registrationStartTime, registrationFinishTime, isSameDate, isDateBefore]);

  // Initialize match start date and time based on registration finish date and time
  useEffect(() => {
    if (calendarDateEndReg && registrationFinishTime && setMatchStartDate && setMatchStart && setValue) {
      // Create a moment object with the registration finish date and time
      const [hours, minutes] = registrationFinishTime.split(':');
      const finishDateTime = moment(calendarDateEndReg)
        .hour(parseInt(hours))
        .minute(parseInt(minutes));
      
      // Add 30 minutes to get match start time
      const matchStartDateTime = finishDateTime.clone().add(30, 'minutes');
      
      // Extract the date and time
      const matchStartDate = matchStartDateTime.toDate();
      const formattedTime = matchStartDateTime.format('HH:mm');
      
      // Set the state values
      setMatchStartDate(matchStartDate);
      setMatchStart(formattedTime);
      
      // Update form values
      setValue('matchStartDate', matchStartDate);
      setValue('matchStart', formattedTime);
    }
  }, [calendarDateEndReg, registrationFinishTime, setMatchStartDate, setMatchStart, setValue]);

  const handleRegistrationStartTimeChange = useCallback((e, setRegistrationStartTime, calendarDateStartReg) => {
    const newTime = e.target.value;
    setRegistrationStartTime(newTime);
    setRegistrationStartDateError(""); // Clear error when time changes
    setRegistrationStartTimeError(""); // Clear registration start time error when time changes
    
    // Validate if registration date is same as tournament start date
    if (isSameDate(calendarDateStartReg, calendarDateStart) && newTime < dateStartTime) {
      setRegistrationStartTimeError(VALIDATION_ERRORS.REGISTRATION_START_TIME_BEFORE_TOURNAMENT_START);
    } else if (isSameDate(calendarDateStartReg, calendarDateEnd) && newTime > dateFinishTime) {
      setRegistrationStartTimeError(VALIDATION_ERRORS.REGISTRATION_START_TIME_AFTER_TOURNAMENT_END);
    } else {
      setRegistrationStartTimeError("");
    }
    
    // Check if registration finish time is now invalid due to start time change
    if (isSameDate(calendarDateStartReg, calendarDateEndReg) && registrationFinishTime < newTime) {
      setRegistrationTimeError(VALIDATION_ERRORS.REGISTRATION_FINISH_TIME_BEFORE_START);
    } else {
      setRegistrationTimeError("");
    }
  }, [calendarDateStart, calendarDateEnd, dateStartTime, dateFinishTime, calendarDateStartReg, calendarDateEndReg, registrationFinishTime, isSameDate]);

  const handleRegistrationFinishTimeChange = useCallback((e, setRegistrationFinishTime, calendarDateEndReg) => {
    const newTime = e.target.value;
    setRegistrationFinishTime(newTime);
    setRegistrationEndDateError(""); // Clear error when time changes
    setRegistrationFinishTimeError(""); // Clear registration finish time error when time changes
    
    // Validate if registration date is same as tournament start date
    if (isSameDate(calendarDateEndReg, calendarDateStart) && newTime < dateStartTime) {
      setRegistrationFinishTimeError(VALIDATION_ERRORS.REGISTRATION_FINISH_TIME_BEFORE_TOURNAMENT_START);
    } else if (isSameDate(calendarDateEndReg, calendarDateEnd) && newTime > dateFinishTime) {
      setRegistrationFinishTimeError(VALIDATION_ERRORS.REGISTRATION_FINISH_TIME_AFTER_TOURNAMENT_END);
    } else {
      setRegistrationFinishTimeError("");
    }
    
    // Validate if registration dates are the same and finish time is before start time
    if (isSameDate(calendarDateStartReg, calendarDateEndReg) && newTime < registrationStartTime) {
      setRegistrationTimeError(VALIDATION_ERRORS.REGISTRATION_FINISH_TIME_BEFORE_START);
    } else {
      setRegistrationTimeError("");
    }
  }, [calendarDateStart, calendarDateEnd, dateStartTime, dateFinishTime, calendarDateStartReg, calendarDateEndReg, registrationStartTime, isSameDate]);

  // Tournament date change handlers
  const handleTournamentStartDateChange = useCallback((date, setCalendarDateStart) => {
    setCalendarDateStart(date);
    setTournamentStartDateError(""); // Clear start date error when date changes
    
    // Auto-update logic: If tournament start is after registration start date,
    // update both registration start and finish dates to match tournament start
    if (date > calendarDateStartReg) {
      setCalendarDateStartReg(date);
      setCalendarDateEndReg(date);
    }
    
    // Auto-update logic: If tournament start is after tournament end date,
    // update tournament end date to match tournament start
    if (date > calendarDateEnd) {
      setCalendarDateEnd(date);
    }
    
    // Validate if finish date is before start date
    if (isDateBefore(calendarDateEnd, date)) {
      setTournamentStartDateError(VALIDATION_ERRORS.TOURNAMENT_START_DATE_ERROR);
      setTournamentFinishDateError(VALIDATION_ERRORS.TOURNAMENT_FINISH_DATE_ERROR);
    } else {
      // Clear errors when dates are valid (same or start before finish)
      setTournamentStartDateError("");
      setTournamentFinishDateError("");
    }
  }, [calendarDateEnd, calendarDateStartReg, setCalendarDateStartReg, setCalendarDateEndReg, setCalendarDateEnd, isDateBefore]);

  const handleTournamentEndDateChange = useCallback((date, setCalendarDateEnd) => {
    setCalendarDateEnd(date);
    setTournamentFinishDateError(""); // Clear finish date error when date changes
    
    // Validate if finish date is before start date
    if (isDateBefore(date, calendarDateStart)) {
      setTournamentStartDateError(VALIDATION_ERRORS.TOURNAMENT_START_DATE_ERROR);
      setTournamentFinishDateError(VALIDATION_ERRORS.TOURNAMENT_FINISH_DATE_ERROR);
    } else {
      // Clear errors when dates are valid (same or finish after start)
      setTournamentStartDateError("");
      setTournamentFinishDateError("");
    }
  }, [calendarDateStart, isDateBefore]);

  // Registration date change handlers
  const handleRegistrationStartDateChange = useCallback((date, setCalendarDateStartReg, setRegistrationStartTime, registrationStartTime) => {
    setCalendarDateStartReg(date);
    setRegistrationStartDateError(""); // Clear error when date changes
    setRegistrationDateError(""); // Clear registration date error when date changes
    
    // Auto-update logic: If registration start is after registration finish date,
    // update registration finish date to match registration start
    if (date > calendarDateEndReg) {
      setCalendarDateEndReg(date);
    }
    
    // Validate time when date changes
    if (isSameDate(date, calendarDateStart) && registrationStartTime < dateStartTime) {
      setRegistrationStartTime(dateStartTime);
    }
    if (isSameDate(date, calendarDateEnd) && registrationStartTime > dateFinishTime) {
      setRegistrationStartTime(dateFinishTime);
    }
  }, [calendarDateStart, calendarDateEnd, dateStartTime, dateFinishTime, calendarDateEndReg, registrationStartTime, isSameDate, setCalendarDateEndReg, isDateBefore]);

  const handleRegistrationEndDateChange = useCallback((date, setCalendarDateEndReg, setRegistrationFinishTime, registrationFinishTime) => {
    setCalendarDateEndReg(date);
    setRegistrationEndDateError(""); // Clear error when date changes
    setRegistrationDateError(""); // Clear registration date error when date changes
    
    // Validate if finish date is before start date
    if (isDateBefore(date, calendarDateStartReg)) {
      setRegistrationDateError(VALIDATION_ERRORS.REGISTRATION_FINISH_BEFORE_START);
    }
    
    // Validate time when date changes
    if (isSameDate(date, calendarDateStart) && registrationFinishTime < dateStartTime) {
      setRegistrationFinishTime(dateStartTime);
    }
    if (isSameDate(date, calendarDateEnd) && registrationFinishTime > dateFinishTime) {
      setRegistrationFinishTime(dateFinishTime);
    }
  }, [calendarDateStart, calendarDateEnd, dateStartTime, dateFinishTime, calendarDateStartReg, registrationFinishTime, isSameDate, isDateBefore]);

  // Tournament time change handlers
  const handleDateStartTimeChange = useCallback((e) => {
    const newTime = e.target.value;
    setDateStartTime(newTime);
    setTournamentStartTimeError(""); // Clear error when time changes
    
    // Check if tournament finish time is now invalid due to start time change
    if (isSameDate(calendarDateStart, calendarDateEnd) && dateFinishTime < newTime) {
      setTournamentFinishTimeError(VALIDATION_ERRORS.TOURNAMENT_FINISH_TIME_BEFORE_START);
    } else {
      setTournamentFinishTimeError("");
    }
  }, [calendarDateStart, calendarDateEnd, dateFinishTime, isSameDate]);

  const handleDateFinishTimeChange = useCallback((e) => {
    const newTime = e.target.value;
    setDateFinishTime(newTime);
    setTournamentFinishTimeError(""); // Clear error when time changes
    
    // Validate if tournament dates are the same and finish time is before start time
    if (isSameDate(calendarDateStart, calendarDateEnd) && newTime < dateStartTime) {
      setTournamentFinishTimeError(VALIDATION_ERRORS.TOURNAMENT_FINISH_TIME_BEFORE_START);
    } else {
      setTournamentFinishTimeError("");
    }
  }, [calendarDateStart, calendarDateEnd, dateStartTime, isSameDate]);

  return {
    registrationStartDateError,
    registrationEndDateError,
    tournamentStartTimeError,
    tournamentFinishTimeError,
    registrationTimeError,
    tournamentStartDateError,
    tournamentFinishDateError,
    registrationDateError,
    registrationStartTimeError,
    registrationFinishTimeError,
    handleRegistrationStartDateChange,
    handleRegistrationEndDateChange,
    handleRegistrationStartTimeChange,
    handleRegistrationFinishTimeChange,
    handleDateStartTimeChange,
    handleDateFinishTimeChange,
    handleTournamentStartDateChange,
    handleTournamentEndDateChange
  };
};
